import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Text } from 'rebass';
import { useHistory } from 'react-router-dom';
import Logo from '../Logo';
import Icon from '../Icon/Icon';
import { logout } from '@/services/auth';
import useSessionInfo from '../../hooks/useSessionInfo';
import useMode from '../../hooks/useMode';
import { navigationConfig } from './navConfig';
import SidebarNav from './SidebarNav';
import SidebarLink from './SidebarLink';
import { useTheme } from 'emotion-theming';
import { Theme } from '@/theme';
import ActivityMenu from '../ActivityFeed/ActivityMenu';
import SidebarActivityAccordion from './SidebarActivityAccordion';

const STORAGE_KEY = 'sidebar-collapsed';
export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 56;

const Sidebar = (): JSX.Element => {
  const { me, session, features } = useSessionInfo();
  const mode = useMode();
  const history = useHistory();
  const theme = useTheme<Theme>();
  const navEntries = navigationConfig(mode, features);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // Close mobile sidebar on navigation
  useEffect(() => {
    return history.listen(() => {
      setMobileOpen(false);
    });
  }, [history]);

  const companyName =
    session?.companyProfile?.name ?? session?.userProfile?.name;

  const profileHref =
    mode === 'individual' ? `/${mode}/profile` : `/${mode}/profile`;

  const buildSidebar = (isCollapsed: boolean, onToggle?: () => void) => (
    <Flex
      flexDirection="column"
      bg="navbar"
      sx={{
        width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        minWidth: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: '100vh',
        boxShadow: '2px 0 6px rgba(0,0,0,0.08)',
        transition: 'width .25s ease, min-width .25s ease',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 10,
        flexShrink: 0,
      }}
    >
      {/* Pinned top: logo + toggle + company name */}
      <Box sx={{ flexShrink: 0 }}>
        <Flex
          alignItems="center"
          px={2}
          sx={{
            height: 48,
            borderBottom: `1px solid ${theme.colors.base.gray[200]}`,
            justifyContent: isCollapsed ? 'center' : 'space-between',
          }}
        >
          {!isCollapsed && (
            <Logo
              padding={0}
              showLogotype
              onClick={() => history.push(`/`)}
              sx={{ maxHeight: 28, cursor: 'pointer' }}
            />
          )}
          {onToggle && (
            <Box
              onClick={onToggle}
              sx={{
                cursor: 'pointer',
                color: theme.colors.onNavbar,
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderRadius: 4,
                flexShrink: 0,
                '&:hover': { color: theme.colors.onNavbarHover },
              }}
            >
              <Icon icon={isCollapsed ? 'list' : 'sidebar-toggle'} size={18} />
            </Box>
          )}
        </Flex>

        {companyName && !isCollapsed && (
          <Box
            px={4}
            py={1}
            sx={{
              borderBottom: `1px solid ${theme.colors.base.gray[200]}`,
              overflow: 'hidden',
            }}
          >
            <Text
              sx={{
                fontSize: 2,
                fontWeight: 'heading',
                color: theme.colors.base.gray[600],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {companyName}
            </Text>
          </Box>
        )}
      </Box>

      <Flex
        flexDirection="column"
        sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
      >
        {!isCollapsed && (
          <>
            <Box
              sx={{
                flex: '0 1 auto',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {navEntries && (
                <SidebarNav
                  entries={navEntries}
                  mode={mode}
                  collapsed={false}
                />
              )}
            </Box>
            <SidebarActivityAccordion mode={mode} />
          </>
        )}
        {isCollapsed && <Box sx={{ flexGrow: 1 }} />}
      </Flex>

      {/* Pinned bottom: user info + logout */}
      {me && (
        <Flex
          alignItems="center"
          px={isCollapsed ? 0 : 3}
          py={3}
          sx={{
            borderTop: `1px solid ${theme.colors.base.gray[200]}`,
            flexShrink: 0,
            gap: 2,
            justifyContent: isCollapsed ? 'center' : 'space-between',
          }}
        >
          {!isCollapsed && (
            <SidebarLink
              to={profileHref}
              collapsed={false}
              exact={false}
              style={{
                padding: 0,
                border: 'none',
                fontSize: theme.fontSizes[1],
                color: theme.colors.onNavbar,
                fontWeight: theme.fontWeights.heading,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexGrow: 1,
                gap: theme.space[2],
              }}
            >
              <Icon icon="person" size={16} style={{ flexShrink: 0 }} />
              {session?.userProfile?.name ?? me.name}
            </SidebarLink>
          )}
          <Flex
            alignItems="center"
            sx={{
              gap: isCollapsed ? 0 : 2,
              flexDirection: isCollapsed ? 'column' : 'row',
              flexShrink: 0,
              overflow: 'visible',
            }}
          >
            {isCollapsed && (
              <ActivityMenu
                menuDomId="sidebar-activity-menu-collapsed"
                panelWidthPx={360}
                placement="top-right"
                caretEdgeInset={24}
              />
            )}
            {isCollapsed && (
              <Box
                as="a"
                href={profileHref}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  history.push(profileHref);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.onNavbar,
                  cursor: 'pointer',
                  p: 1,
                  '&:hover': { color: theme.colors.onNavbarHover },
                }}
              >
                <Icon icon="person" size={18} />
              </Box>
            )}
            {!isCollapsed && (
              <Box
                onClick={logout}
                title="Log out"
                sx={{
                  cursor: 'pointer',
                  color: theme.colors.onNavbar,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  '&:hover': { color: theme.colors.onNavbarHover },
                }}
              >
                <Icon icon="logout" size={16} />
              </Box>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );

  return (
    <>
      {/* Mobile backdrop (shown when sidebar is open/expanded on mobile) */}
      {mobileOpen && (
        <Box
          onClick={() => setMobileOpen(false)}
          sx={{
            display: ['block', 'block', 'none'],
            position: 'fixed',
            inset: 0,
            bg: 'rgba(0,0,0,0.4)',
            zIndex: 19,
          }}
        />
      )}

      {/* Desktop sidebar (always visible, respects collapsed state) */}
      <Box
        sx={{
          display: ['none', 'none', 'flex'],
          flexShrink: 0,
          width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          transition: 'width .25s ease',
        }}
      >
        {buildSidebar(collapsed, toggleCollapsed)}
      </Box>

      {/* Mobile sidebar: collapsed strip always visible, expands as overlay when mobileOpen */}
      <Box
        sx={{
          display: ['flex', 'flex', 'none'],
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 20,
        }}
      >
        {mobileOpen
          ? buildSidebar(false, () => setMobileOpen(false))
          : buildSidebar(true, () => setMobileOpen(true))}
      </Box>
    </>
  );
};

export default Sidebar;
